import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageCard } from '../../Compounds/CardWithTitle';
import { PageLayout } from '../../PageLayout';
import {
  dropProjectRisk,
  fetchProjects,
  getProjectLoadingSubject,
  getProjectSubject,
  Project
} from '../../../store/Project';
import { TabProps } from '../Inventory';
import {
  getCustomViewSubject,
  getEffectiveAccountId,
  getSelectedAccountsSubject,
  getUser,
  getUserPreferencesSubject,
  GridPreferences,
  isCapabilityDenied,
  isInternalUser,
  isProjectsUpdatesEnabled,
  isRelatedInventoryReportsEnabled,
  PrefKey,
  UserPreferencesContent
} from '../../../store/User';
import { CustomFieldCapability } from '../../../utils/customFieldCapabilities';
import { EMPTY_GRID, useAccountGridConfig, useAccountGridReady } from '../../../store/AccountCustomization';
import { refreshProjectsSummaryStats, useProjectsSummaryStats } from '../../../store/ProjectsSummaryStats';
import { ProjectsSummaryStatsWidget } from './ProjectsSummaryStatsWidget';
import { refreshAtRiskReport, useAtRiskProjects } from '../../../store/AtRiskProjects';
import { AtRiskSection } from './AtRiskSection';
import { StatusUpdateSection } from '../../Compounds/StatusUpdate';
import { useInventory } from '../../../store/Inventory';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { getGridColumns } from '../../Compounds/UpstackDataGrid/helpers';
import {
  accountVisibilityChanges,
  withAccountDefaultLayout
} from '../../Compounds/UpstackDataGrid/accountDefaultLayout';
import {
  FAILED_SAVE,
  createCustomFieldColumns,
  customFieldCellEditable,
  makeCustomFieldRowUpdate,
  savedDateMovesRisk
} from '../../Compounds/UpstackDataGrid/customFieldColumns';
import { foreignRowAccountIds } from '../../Compounds/UpstackDataGrid/foreignRowAccountIds';
import { useCustomFieldAcceptance } from '../../../Api/Admin/CustomFields/useCustomFieldAcceptance';
import { projectGridColumns, projectRowAccountId } from './projectGridColumnsDefs';
import { projectsInventoryModeColumns } from './ProjectsRelatedInventoryGridColumnDefs';
import { getStoredFlag, storeFlag } from '../../../utils/browserStoredFlag';
import {
  useProjectsRelatedInventory,
  setProjectsRelatedInventoryCustomFields,
  RelatedInventoryRow,
  refreshProjectsRelatedInventory,
  refreshProjectsRelatedInventoryIfRunning
} from '../../../store/ProjectsRelatedInventory';
import { RelatedInventoryRiskItem } from '../../../Api/Projects/projectsApi';
import { inventoryRowAccountId } from '../Inventory/inventoryGridColumnDefs';
import { LabelledToggle } from '../../Compounds/LabelledToggle';
import { CustomFieldDefinition } from '../../../types';
import { fetchCustomFieldDefinitionsApi } from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { useFeatureFlags } from '../../../Api/useFeatureFlags';
import { TabPanel } from '../../Atoms/TabPanel';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { parseProjectScopedViewName } from '../../Compounds/CustomTableViews/viewKey';
import { DashboardWidgets } from '../../Compounds/DashboardWidgets';
import { CustomizeDashboardDrawer } from '../../Compounds/CustomizeDashboardDrawer';
import { GridTabsHeader } from '../../Compounds/GridTabsHeader';
import { SavedViewControls } from '../../Compounds/CustomTableViews/SavedViewControls';

const DISPLAY_AS_INVENTORY_KEY = 'projectsDisplayAsInventory';

// One empty list for every render, or the columns memoized on it, and the grid with them, rebuild each time.
const EMPTY_DISABLED_FIELDS: string[] = [];

interface ProjectsTabsProps extends TabProps {
  projects?: Project[];
  inventory?: RelatedInventoryRow[];
}

const inactiveProjectStatus = ['Completed', 'Cancelled'];
// Raw Salesforce statuses: the Status column shows Completed as Active, so the tabs cannot follow that word.
const completedSalesforceAssetStatus = ['Completed', 'Cancelled', 'Disconnected', 'Changed'];

const partitionRows = <T,>(rows: T[], isCompleted: (row: T) => boolean): [T[], T[]] => {
  const active: T[] = [];
  const completed: T[] = [];
  rows.forEach((row) => (isCompleted(row) ? completed : active).push(row));
  return [active, completed];
};

export const ProjectsPage = () => {
  const page = 'Projects';
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const [tabs, setTabs] = useState<ProjectsTabsProps[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [allTabs, setAllTabs] = useState<ProjectsTabsProps[]>([]);
  const [customTabNames, setCustomTabNames] = useState<string[]>([]);
  const [userPreferences, setUserPreferences] = useState<UserPreferencesContent>();
  const { config: featureFlags } = useFeatureFlags();
  const projectsUpdatesEnabled = isProjectsUpdatesEnabled(featureFlags);
  const relatedInventoryReportsEnabled = isRelatedInventoryReportsEnabled(featureFlags);
  const accountId = getEffectiveAccountId();
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const customFieldDefsRef = useRef<CustomFieldDefinition[]>([]);
  const [displayAsInventory, setDisplayAsInventory] = useState<boolean>(() => getStoredFlag(DISPLAY_AS_INVENTORY_KEY));

  const { setSnack } = useSnackbar();
  const { fetchInventory } = useInventory();
  const { stats, fetchStats } = useProjectsSummaryStats();
  const { atRiskReport, fetchAtRiskReport } = useAtRiskProjects();
  const {
    relatedInventory,
    loading: relatedInventoryLoading,
    fetchProjectsRelatedInventory
  } = useProjectsRelatedInventory();

  const inventoryRows = useMemo(() => relatedInventory ?? [], [relatedInventory]);
  // Inventory mode is the project detail's Related Inventory table widened to every project, so it
  // reads that grid's account configuration, custom field entity and saved views rather than its own.
  const gridKey = displayAsInventory ? 'related_project_assets_grid' : 'project_grid';
  const customFieldEntityType = displayAsInventory ? 'ProjectDetail' : 'Project';
  // Scheduling related-inventory reports is gated by the flag; projects mode always offers it. Save As
  // View is a client-side column layout with no backend, so it stays available in either mode.
  const showScheduleReport = !displayAsInventory || relatedInventoryReportsEnabled;

  // Held in a ref so the callback identity is stable: the hook hands back a fresh function every
  // render, and a changing one would rebuild the memoized columns on each of them.
  const fetchRelatedInventoryRef = useRef(fetchProjectsRelatedInventory);
  fetchRelatedInventoryRef.current = fetchProjectsRelatedInventory;
  const refetchRelatedInventory = useCallback(async () => fetchRelatedInventoryRef.current(true), []);
  // Read through a ref by the memoized grid save, which must not change identity as the flags arrive.
  const refreshRiskRef = useRef(() => {});
  refreshRiskRef.current = () => {
    void refreshProjectsRelatedInventory();
    if (!projectsUpdatesEnabled) return;
    void refreshProjectsSummaryStats();
    void refreshAtRiskReport();
  };

  const handleDisplayAsInventoryChange = (enabled: boolean) => {
    setDisplayAsInventory(enabled);
    storeFlag(DISPLAY_AS_INVENTORY_KEY, enabled);
    setSelectedTab(0);
    setSelectedCustomView('');
  };

  // A custom view is tracked by name (selectedCustomView); its panel index is derived, not stored, so
  // deleting a different view can't strand the grid on a now-out-of-range index.
  const handleTabChange = (index: number, name: string, customTab?: boolean) => {
    if (customTab) {
      setSelectedCustomView(name);
    } else {
      setSelectedCustomView('');
      setSelectedTab(index);
    }
  };

  const handleNewTabModalOpen = () => {
    setTriggerSave(false);
    setCreateOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const projectsSubject = getProjectSubject().subscribe((data) => {
      setProjects(data);
      const [activeProjects, completedProjects] = partitionRows(data, (p) => inactiveProjectStatus.includes(p.status));
      setTabs([
        {
          name: 'Active',
          projects: activeProjects,
          customView: false,
          count: activeProjects.length
        },
        {
          name: 'Completed',
          projects: completedProjects,
          customView: false,
          count: completedProjects.length
        }
      ]);
    });
    const projectsLoadingSubject = getProjectLoadingSubject().subscribe((loading) => setProjectsLoading(loading));
    const userPreferencesSubject = getUserPreferencesSubject().subscribe((prefs) => setUserPreferences(prefs.content));

    fetchInventory();
    fetchProjects();

    return () => {
      if (projectsSubject) projectsSubject.unsubscribe();
      if (projectsLoadingSubject) projectsLoadingSubject.unsubscribe();
      if (userPreferencesSubject) userPreferencesSubject.unsubscribe();
    };
  }, []);

  // Both re-subscribe on a mode flip: each mode reads its own grid's saved views and its own
  // custom field entity, so the previous mode's set must not linger in the new one's columns.
  useEffect(() => {
    const customViewSub = getCustomViewSubject().subscribe((customViews) => {
      setCustomTabNames(
        customViews
          .filter((view) => view.page === `${gridKey}_`)
          // Only related-inventory views are per-project (UUID-prefixed) and belong on a project's detail
          // page, not the account list; project-type views keep their names verbatim.
          .filter((view) => (displayAsInventory ? parseProjectScopedViewName(view.name) === null : true))
          .map((v) => v.name)
      );
    });
    return () => customViewSub.unsubscribe();
  }, [gridKey, displayAsInventory]);

  useEffect(() => {
    let current = true;
    // Drop the previous entity's definitions up front: until the new response lands they would
    // paint the wrong mode's columns, and a cell edited in that window would post a definition id
    // the API rejects for this entity type.
    customFieldDefsRef.current = [];
    setCustomFieldDefs([]);

    const customFieldAccountsSub = getSelectedAccountsSubject().subscribe(() => {
      const accountId = getEffectiveAccountId();
      if (!accountId) {
        customFieldDefsRef.current = [];
        setCustomFieldDefs([]);
        return;
      }
      fetchCustomFieldDefinitionsApi(accountId, undefined, customFieldEntityType).then(({ data }) => {
        // Dropped when a mode flip outran the response, so the other entity's defs can't land here.
        if (!current) return;
        // Clear on a failed/empty response so a prior account's columns don't linger after a switch.
        customFieldDefsRef.current = data ?? [];
        setCustomFieldDefs(data ?? []);
      });
    });

    return () => {
      current = false;
      customFieldAccountsSub.unsubscribe();
    };
  }, [customFieldEntityType]);

  // BehaviorSubject fires on subscribe, covering the first entry into Inventory mode and account changes alike.
  useEffect(() => {
    if (!displayAsInventory) return;
    const accountsSub = getSelectedAccountsSubject().subscribe(() => {
      void fetchRelatedInventoryRef.current(true);
    });
    return () => accountsSub.unsubscribe();
  }, [displayAsInventory]);

  const effectiveAccountId = getEffectiveAccountId();
  const accountGridConfig = useAccountGridConfig(effectiveAccountId, gridKey);

  // The grid restores pinning only when gridSettings changes identity, so a user with nothing saved
  // under either mode's key must not get one shared empty object across a flip — the Risk pin would
  // never apply. Cached per key so an unsaved tab still keeps a stable reference between renders.
  const emptyGridByPage = useRef<Record<string, GridPreferences>>({});
  const gridPropsFor = (pageKey: string) => {
    const saved = userPreferences?.[pageKey as PrefKey] as GridPreferences | undefined;
    if (saved) return accountVisibilityChanges(saved, accountGridConfig);
    emptyGridByPage.current[pageKey] ??= { ...EMPTY_GRID };
    return { gridSettings: emptyGridByPage.current[pageKey] };
  };
  const accountConfigReady = useAccountGridReady(effectiveAccountId);

  // Account-hidden columns, enforced on every render so an auto-saved personal view can't re-show them.
  // Staff read the account config live; customers get it from the delivered project_grid base key.
  const accountDisabledFields = useMemo(
    () => accountGridConfig?.disabledFields ?? EMPTY_DISABLED_FIELDS,
    [accountGridConfig]
  );
  const accountLayout = accountGridConfig?.muiConfig?.columns;

  // Filtered by the base key, not a stale per-tab one, and derived in render so a mode flip brings its columns along.
  const columns = useMemo(() => {
    const modeColumns: GridSingleSelectColDef[] = displayAsInventory
      ? [
          ...getGridColumns(
            inventoryRows,
            projectsInventoryModeColumns(true, refetchRelatedInventory),
            gridKey,
            accountDisabledFields
          ),
          ...createCustomFieldColumns<RelatedInventoryRiskItem>(customFieldDefs, accountDisabledFields)
        ]
      : [
          ...getGridColumns(projects, projectGridColumns(projectsUpdatesEnabled), gridKey, accountDisabledFields),
          ...createCustomFieldColumns<Project>(customFieldDefs, accountDisabledFields)
        ];
    return withAccountDefaultLayout(modeColumns, accountLayout);
  }, [
    displayAsInventory,
    inventoryRows,
    gridKey,
    refetchRelatedInventory,
    projects,
    projectsUpdatesEnabled,
    customFieldDefs,
    accountDisabledFields,
    accountLayout
  ]);

  const handleProjectCustomFieldRowUpdate = useMemo(
    () =>
      makeCustomFieldRowUpdate<Project>({
        entityType: 'Project',
        getDefinitions: () => customFieldDefsRef.current,
        persist: (project) => {
          const current = getProjectSubject().getValue();
          getProjectSubject().next(current.map((p) => (p.id === project.id ? project : p)));
        }
      }),
    []
  );

  const handleInventoryCustomFieldRowUpdate = useMemo(
    () =>
      makeCustomFieldRowUpdate<RelatedInventoryRiskItem>({
        entityType: 'ProjectDetail',
        getDefinitions: () => customFieldDefsRef.current,
        // Persist to the store the grid re-reads on remount, or navigating back shows stale values.
        persist: (asset, saved) => {
          setProjectsRelatedInventoryCustomFields(asset.id, asset.custom_fields ?? []);
          if (!savedDateMovesRisk(saved)) {
            void refreshProjectsRelatedInventoryIfRunning();
            return;
          }
          if (asset.projectId) dropProjectRisk(asset.projectId);
          refreshRiskRef.current();
        }
      }),
    []
  );

  const rowAccountId = displayAsInventory ? inventoryRowAccountId : projectRowAccountId;
  const otherRowAccountIds = useMemo(
    () =>
      displayAsInventory
        ? foreignRowAccountIds(inventoryRows, inventoryRowAccountId, effectiveAccountId)
        : foreignRowAccountIds(projects, projectRowAccountId, effectiveAccountId),
    [displayAsInventory, inventoryRows, projects, effectiveAccountId]
  );
  const acceptance = useCustomFieldAcceptance(otherRowAccountIds, customFieldEntityType);

  // Rebuilt each render rather than memoized so an account switch can never leave the gate on the previous account's set.
  const customFieldCellIsEditable = customFieldCellEditable({
    definitionsAccountId: effectiveAccountId,
    rowAccountId,
    acceptance,
    unrestricted: isInternalUser(getUser()),
    denied: isCapabilityDenied(CustomFieldCapability.VALUES_UPSERT, getUser()),
    definitions: customFieldDefs
  });

  const handleCustomFieldRowUpdateError = useCallback(
    (error: unknown) => {
      const message = error instanceof Error ? error.message : FAILED_SAVE;
      setSnack({ message, open: true, type: 'error' });
    },
    [setSnack]
  );

  // BehaviorSubject fires on subscribe, covering the initial fetch and account changes alike.
  useEffect(() => {
    if (!projectsUpdatesEnabled) return;
    const accountsSub = getSelectedAccountsSubject().subscribe(() => {
      void fetchStats(true);
      void fetchAtRiskReport(true);
    });
    return () => accountsSub.unsubscribe();
  }, [projectsUpdatesEnabled]);

  const inventoryTabs: ProjectsTabsProps[] = useMemo(() => {
    const [active, completed] = partitionRows(inventoryRows, (row) =>
      completedSalesforceAssetStatus.includes(row.rawStatus ?? '')
    );
    return [
      { name: 'Active', inventory: active, count: active.length, customView: false },
      { name: 'Completed', inventory: completed, count: completed.length, customView: false }
    ];
  }, [inventoryRows]);
  const baseTabs = displayAsInventory ? inventoryTabs : tabs;

  // Derived each render from the selected view's name, so a delete/rename that shifts the custom
  // indices can't leave the active panel pointing at the wrong (or a removed) tab.
  const customIndex = selectedCustomView ? customTabNames.indexOf(selectedCustomView) : -1;
  const activeTab = customIndex >= 0 ? baseTabs.length + customIndex : selectedTab;

  useEffect(() => {
    if (userPreferences) {
      const customTabs: TabProps[] = customTabNames.map((t) => ({ name: t, count: 0, customView: true }));

      if (selectedCustomView && !customTabNames.includes(selectedCustomView)) {
        setSelectedCustomView('');
        handleTabChange(0, 'Active');
      }

      setAllTabs(baseTabs.length ? baseTabs.concat(customTabs) : []);
    }
    // Keyed on the names, not the count: a mode flip swaps each mode's saved views for the other's, and
    // a rename replaces one in place — both leave the count untouched while every tab name changes.
  }, [userPreferences, baseTabs, customTabNames]);

  // Lifted out of the JSX so the card's prop stays a plain value — nested inline it would push the
  // tree past the max-JSX-depth rule.
  const displayAsInventoryToggle = (
    <LabelledToggle
      id="display-as-inventory-toggle"
      dataTestId="display-as-inventory"
      label="Display as Inventory"
      checked={displayAsInventory}
      onChange={handleDisplayAsInventoryChange}
    />
  );

  return (
    <PageLayout pageTitle={page}>
      <>
        {projectsUpdatesEnabled && (
          <div className="mt-8 flex flex-col gap-4 rounded-lg bg-[#333741] p-5">
            {stats && <ProjectsSummaryStatsWidget stats={stats} />}
            <StatusUpdateSection
              entityType="Account"
              entityId={accountId ?? undefined}
              emptyEntityPrompt="Select a single account to generate a summary."
            />
          </div>
        )}
        {projectsUpdatesEnabled && <AtRiskSection report={atRiskReport} />}
        {!projectsUpdatesEnabled && (
          <PageCard
            title="Dashboard"
            cardActions={<CustomizeDashboardDrawer title="Projects" />}>
            <DashboardWidgets
              inventoryAssets={[]}
              pageTitle={page}
              cases={[]}
              projects={projects}
            />
          </PageCard>
        )}
        <PageCard
          cardActions={displayAsInventoryToggle}
          childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
          title={page}>
          <>
            <GridTabsHeader
              labels={baseTabs.map((tab) => `${tab.name} (${tab.count})`)}
              selectedTab={activeTab}
              onTabChange={(index) => handleTabChange(index, allTabs?.[index]?.name)}>
              <SavedViewControls
                selectedCustomView={selectedCustomView}
                customTabs={customTabNames}
                onSelectView={(name) => handleTabChange(0, name, true)}
                onSaveAsView={() => setTriggerSave(true)}
                saving={triggerSave}
                onScheduleReport={showScheduleReport ? () => setOpen(true) : undefined}
              />
            </GridTabsHeader>
            {allTabs.map((tab, index) => (
              <TabPanel
                key={`${index}`}
                index={index}
                value={activeTab}>
                <UpstackDataGrid
                  dataCy="projects-data-grid"
                  columns={columns}
                  {...gridPropsFor(`${gridKey}_${tab.name}`)}
                  handleNewTabModalOpen={handleNewTabModalOpen}
                  // Null rows means the first fetch has not been kicked off yet — the effect that
                  // starts it runs after this render, so reading it as loaded would flash an empty grid.
                  loadingData={
                    !accountConfigReady ||
                    (displayAsInventory ? relatedInventoryLoading || relatedInventory === null : projectsLoading)
                  }
                  page={`${gridKey}_${tab.name}`}
                  // Pinning Risk keeps it first even for users whose saved column order
                  // predates the move (order reconciliation never re-pins saved fields).
                  pinnedColumns={{ left: displayAsInventory ? ['details', 'riskLabel'] : ['details'] }}
                  processRowUpdate={
                    displayAsInventory ? handleInventoryCustomFieldRowUpdate : handleProjectCustomFieldRowUpdate
                  }
                  onProcessRowUpdateError={handleCustomFieldRowUpdateError}
                  isCellEditable={customFieldCellIsEditable}
                  rows={displayAsInventory ? tab.inventory || inventoryRows : tab.projects || projects}
                  showSearch
                  title={displayAsInventory ? `Related Inventory - ${tab.name}` : `Projects - ${tab.name}`}
                  triggerSave={triggerSave}
                />
              </TabPanel>
            ))}
          </>
        </PageCard>
        <div className="h-2"></div>
      </>
      <ViewsModal
        open={open}
        handleClose={handleClose}
        type={displayAsInventory ? 'related_project_assets' : 'project'}
      />
      <NewViewModal
        open={createOpen}
        setOpen={setCreateOpen}
        sourceTab={allTabs[activeTab]?.name || 'Active'}
        type={displayAsInventory ? 'related_project_assets' : 'project'}
      />
    </PageLayout>
  );
};
